import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Button from '../../components/Button';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import { toggleModal } from '../../reducers/modalToggle';
import ModalComponent from '../../components/ModalComponent';
import ConfirmCreateUserModal from '../../components/ConfirmCreateUserModal';
import moment from 'moment';

interface EmployeeOnboardingStepTwoProps {
  otherClasses?: string;
  formHandler: any;
  isLoading: boolean;
  handleSteps: React.Dispatch<number>;
  roles: {
    label: string;
    value: string;
  }[];
  departments: {
    label: string;
    value: string;
  }[];
}

const EmployeeOnboardingStepTwo: React.FC<EmployeeOnboardingStepTwoProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  departments,
  roles,
  isLoading,
}) => {
  const employeeOnboardingStepTwoClasses = clsx(otherClasses);
  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  return (
    <div
      className={employeeOnboardingStepTwoClasses}
      data-testid="employee-onboarding-step-two"
    >
      <div className="flex max-w-818 items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Review Employee Details</h1>

          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Review the employee details. By submitting, you agree to the terms
            and confirm the details are accurate.
          </p>
        </div>
      </div>
      <div className="lg:mt-9">
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-input mt-15 text-base leading-5 font-normal italic lg:hidden">
          Review the employee details. By submitting, you agree to the terms and
          confirm the details are accurate.
        </p>
        <div className="lg:border-t-neutral-tones-text/10 py-8 lg:border-t">
          <div className="flex max-w-950 flex-col items-start justify-between gap-9 md:flex-row md:gap-12 lg:flex-wrap lg:gap-x-100 lg:gap-y-8">
            <div className="flex w-full flex-col md:w-auto">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Personal Details
              </h6>
              <div className="grid grid-cols-2 gap-x-75 gap-y-6">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Name
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.fullName}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Email
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.email}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Contact
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.contact}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Date Of Birth
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {moment(formHandler?.values?.dob).format('MMM DD YYYY')}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Date Of Hire
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {moment(formHandler?.values?.dateOfHire).format(
                      'MMM DD YYYY',
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="-order-1 flex flex-col lg:order-none">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Internal Details
              </h6>
              <div className="flex items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Employee ID
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.employeeId}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold lg:text-xl lg:leading-6">
                Role
              </h6>
              <h3 className="text-neutral-tones-text text-3xl uppercase">
                {
                  roles?.find(
                    (node) => node?.value === formHandler?.values?.role,
                  )?.label
                }
              </h3>
            </div>
          </div>
        </div>
        <div className="border-y-neutral-tones-text/10 border-y py-8">
          <div className="grid max-w-950 grid-cols-1 items-start gap-9 md:grid-cols-2 md:gap-12 lg:gap-100">
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Designation
              </h6>
              <div className="grid grid-cols-2 items-start gap-75 md:gap-12 lg:gap-75">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Department
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {
                      departments?.find(
                        (node) =>
                          node?.value === formHandler?.values?.department,
                      )?.label
                    }
                    {/* {formHandler?.values?.department} */}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Job Title
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.jobTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex items-center gap-8">
          <Button
            onClick={() => handleSteps(1)}
            size={useButtonSizeSmaller()}
            type="button"
            variant="secondary"
            otherClasses="max-w-211 w-full"
          >
            <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" />{' '}
            Go Back
          </Button>
          <Button
            type="button"
            onClick={() => dispatch(toggleModal('confirm-create-user'))}
            size={useButtonSizeSmaller()}
            otherClasses="max-w-211 w-full"
          >
            Confirm
            <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
          </Button>
        </div>
      </div>
      {modalName === 'confirm-create-user' && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 "
          additionalClass={clsx(
            'w-full max-w-573 rounded-2xl p-5 lg:px-9 lg:py-6 ',
          )}
        >
          <ConfirmCreateUserModal
            isDisable={isLoading}
            formHandler={formHandler}
            handleClose={() => dispatch(toggleModal(''))}
          />
        </ModalComponent>
      )}
    </div>
  );
};

export default EmployeeOnboardingStepTwo;
